/**
 * Mobile Attachment Upload Page (Day-15 Module 9)
 * 
 * Standalone page for uploading attachments for various purposes:
 * - Incident photos
 * - Maintenance photos
 * - Medication proof
 * - Document uploads
 * - Compliance evidence
 *
 * BS3-025: `portal` renders the same upload inside the desktop Staff Portal (/staff/attachments) — the portal shell supplies
 * the title, so no mobile header, no bar fixed to the bottom of the screen, and a wider category grid.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { ArrowLeft, Upload, FileImage, Clock, CheckCircle, AlertTriangle } from 'lucide-react';
import { isOnline } from '@/lib/offline/syncEngine';
import { SyncStatusBadge, OfflineIndicator, SYNC_STATUS } from '@/components/mobile/SyncStatusBadge';
import MobileAttachmentCapture from '@/components/mobile/MobileAttachmentCapture';
import { 
  ATTACHMENT_ENTITY_TYPES, 
  ATTACHMENT_STATUS,
  getAttachmentStats,
} from '@/lib/offline/attachmentStorage';

const ENTITY_TYPE_OPTIONS = [
  { value: 'INCIDENT', label: 'Incident Photos', icon: '⚠️', description: 'Document safety incidents' },
  { value: 'MAINTENANCE', label: 'Maintenance Photos', icon: '🔧', description: 'Property issues & repairs' },
  { value: 'MEDICATION', label: 'Medication Proof', icon: '💊', description: 'Evidence of administration' },
  { value: 'DOCUMENT', label: 'Document Upload', icon: '📄', description: 'General documents' },
  { value: 'COMPLIANCE', label: 'Compliance Evidence', icon: '✓', description: 'Training & certifications' },
  { value: 'PROGRESS_NOTE', label: 'Note Attachments', icon: '📝', description: 'Progress note photos' },
];

export default function MobileAttachmentUploadPage({ portal = false }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const presetEntityType = searchParams.get('entityType');
  const presetEntityId = searchParams.get('entityId');

  const [online, setOnline] = useState(isOnline());
  const [entityType, setEntityType] = useState(presetEntityType || '');
  const [notes, setNotes] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [stats, setStats] = useState({ pending: 0, synced: 0, failed: 0 });

  // Online/offline listener
  useEffect(() => {
    const handleOnline = () => setOnline(true);
    const handleOffline = () => setOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Load stats
  useEffect(() => {
    const loadStats = async () => {
      const s = await getAttachmentStats();
      setStats(s);
    };
    loadStats();

    // Listen for attachment queue updates
    const handleQueueUpdate = () => loadStats();
    window.addEventListener('attachmentQueueUpdated', handleQueueUpdate);
    return () => window.removeEventListener('attachmentQueueUpdated', handleQueueUpdate);
  }, []);

  const handleAttachmentsChange = (newAttachments) => {
    setAttachments(newAttachments);
  };

  const handleDone = () => {
    if (attachments.length === 0) {
      toast.info('No attachments added');
      navigate(-1);
      return;
    }

    const pendingCount = attachments.filter(a => a.status === ATTACHMENT_STATUS.PENDING).length;
    const syncedCount = attachments.filter(a => a.status === ATTACHMENT_STATUS.SYNCED).length;

    if (syncedCount === attachments.length) {
      toast.success(`${syncedCount} attachment(s) uploaded successfully`);
    } else if (pendingCount > 0) {
      toast.success(`${attachments.length} attachment(s) saved`, {
        description: online ? 'Uploading...' : 'Will upload when online',
      });
    }

    navigate(-1);
  };

  const selectedTypeInfo = ENTITY_TYPE_OPTIONS.find(t => t.value === entityType);

  return (
    <div className={portal ? 'space-y-4' : 'min-h-screen bg-background pb-24'} data-testid={portal ? 'staff-attachment-upload-page' : 'mobile-attachment-upload-page'}>
      {/* Header (mobile only — the Staff Portal shell shows the title) */}
      {!portal && (
      <div className="sticky top-0 z-10 bg-primary text-primary-foreground px-4 py-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" className="text-primary-foreground hover:bg-primary-foreground/10" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <FileImage className="h-5 w-5" />
              <h1 className="text-lg font-bold">Upload Attachments</h1>
            </div>
            <p className="text-xs opacity-80">Capture photos or upload documents</p>
          </div>
        </div>
      </div>
      )}

      {/* Stats Banner */}
      <div className={portal ? 'rounded-lg border bg-muted/30 px-4 py-3' : 'bg-muted/50 border-b px-4 py-3'} data-testid="attachment-stats">
        <div className="grid grid-cols-3 gap-2 text-center">
          <div>
            <p className="text-2xl font-bold text-amber-600">{stats.pending}</p>
            <p className="text-xs text-muted-foreground">Pending</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-green-600">{stats.synced}</p>
            <p className="text-xs text-muted-foreground">Uploaded</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-red-600">{stats.failed}</p>
            <p className="text-xs text-muted-foreground">Failed</p>
          </div>
        </div>
      </div>

      {/* Offline/Sync Status */}
      <div className={portal ? 'space-y-2' : 'px-4 pt-4 space-y-2'}>
        <OfflineIndicator isOffline={!online} />
      </div>

      {/* Content */}
      <div className={portal ? 'space-y-4' : 'p-4 space-y-4'}>
        {/* Entity Type Selection */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">What are you uploading?</CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <div className={portal ? 'grid grid-cols-2 lg:grid-cols-3 gap-2' : 'grid grid-cols-2 gap-2'}>
              {ENTITY_TYPE_OPTIONS.map(({ value, label, icon, description }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setEntityType(value)}
                  className={`p-3 rounded-lg border-2 text-left transition-all ${
                    entityType === value
                      ? 'border-primary bg-primary/5'
                      : 'border-transparent bg-muted/50 hover:bg-muted'
                  }`}
                  data-testid={`entity-type-${value.toLowerCase()}`}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{icon}</span>
                    <span className="text-sm font-medium">{label}</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">{description}</p>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Attachment Capture (shown when type selected) */}
        {entityType && (
          <>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <span>{selectedTypeInfo?.icon}</span>
                  {selectedTypeInfo?.label}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 pt-0">
                <MobileAttachmentCapture
                  entityType={entityType}
                  entityId={presetEntityId}
                  maxAttachments={10}
                  acceptedTypes="image/*,application/pdf"
                  onAttachmentsChange={handleAttachmentsChange}
                  initialAttachments={attachments}
                />
              </CardContent>
            </Card>

            {/* Notes */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Notes (Optional)</CardTitle>
              </CardHeader>
              <CardContent className="p-4 pt-0">
                <Textarea
                  placeholder="Add any notes about these attachments..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  className="resize-none"
                  data-testid="attachment-notes"
                />
              </CardContent>
            </Card>
          </>
        )}

        {/* Instructions (shown when no type selected) */}
        {!entityType && (
          <Card className="border-dashed">
            <CardContent className="p-8 text-center">
              <Upload className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
              <p className="text-muted-foreground">
                Select a category above to start uploading
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Action Bar — fixed to the bottom on mobile, inline at the end of the page in the Staff Portal */}
      <div className={portal ? 'flex justify-end' : 'fixed bottom-0 left-0 right-0 bg-background border-t p-4 safe-area-inset-bottom'} data-testid="attachment-actions">
        <div className={portal ? 'flex gap-2' : 'flex gap-3'}>
          <Button 
            variant="outline" 
            className={portal ? '' : 'flex-1'}
            onClick={() => navigate(-1)}
          >
            Cancel
          </Button>
          <Button 
            className={portal ? '' : 'flex-1'}
            onClick={handleDone}
            disabled={!entityType}
            data-testid="done-upload-btn"
          >
            <CheckCircle className="h-4 w-4 mr-2" />
            Done ({attachments.length})
          </Button>
        </div>
      </div>
    </div>
  );
}
